"use client"

import { useState, useEffect, Fragment, useCallback } from "react"
import { PlusCircle, Eye, Zap, FileText, RefreshCw, X, Menu, Package, ClipboardCheck, AlertTriangle, FileStack, History } from "lucide-react"
import { toast } from "react-toastify"
import Select from "react-select"
import { Dialog, Transition } from "@headlessui/react"
import Topbar from "../../components/TopbarNav";
import MasterSideBar from "../../components/MasterSideBar";
import PremiumHeader from "../../components/common/PremiumHeader";
import PremiumCard from "../../components/common/PremiumCard";

import {
    fetchRequests,
    fetchEquipment,
    reportIssue,
    createRequest,
    createFormRequest,
} from "../../api/Sprocurement-api"

// --- Constants ---
const REQUISITION_STATUS_OPTIONS = [
    { value: "all", label: "All Statuses" },
    { value: "draft", label: "Draft" },
    { value: "submitted", label: "Submitted" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
    { value: "processing", label: "Processing" },
    { value: "completed", label: "Completed" },
]

const ITEM_CATEGORY_OPTIONS = [
    { value: "office_supplies", label: "Office Supplies" },
    { value: "it_equipment", label: "IT Equipment" },
    { value: "field_equipment", label: "Field Equipment" },
    { value: "furniture", label: "Furniture" },
    { value: "vehicles", label: "Vehicles & Parts" },
    { value: "other", label: "Other" },
]

const ISSUE_TYPE_OPTIONS = [
    { value: "broken", label: "Broken / Damaged" },
    { value: "malfunctioning", label: "Malfunctioning" },
    { value: "lost", label: "Lost" },
    { value: "maintenance", label: "Maintenance Request" },
]

const ProgramStaffProcurement = () => {
    const [activeTab, setActiveTab] = useState("myRequests")
    const [loading, setLoading] = useState(false)
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
    const [requests, setRequests] = useState([])
    const [assignedEquipment, setAssignedEquipment] = useState([])
    const [requestToView, setRequestToView] = useState(null)

    // Form States
    const [newRequestTitle, setNewRequestTitle] = useState("")
    const [newRequestCategory, setNewRequestCategory] = useState(null)
    const [newRequestDescription, setNewRequestDescription] = useState("")
    const [issueEquipment, setIssueEquipment] = useState("")
    const [issueType, setIssueType] = useState(null)
    const [issueDescription, setIssueDescription] = useState("")
    const [formName, setFormName] = useState("")
    const [formQuantity, setFormQuantity] = useState(1)
    const [formReason, setFormReason] = useState("")

    const fetchAllData = useCallback(async () => {
        setLoading(true)
        const token = localStorage.getItem("token")
        if (!token) return
        try {
            const [reqData, eqData] = await Promise.all([fetchRequests(token), fetchEquipment(token)])
            setRequests(reqData || [])
            setAssignedEquipment(eqData || [])
        } catch (err) { toast.error("Failed to sync data") } finally { setLoading(false) }
    }, [])

    useEffect(() => { fetchAllData() }, [fetchAllData])

    const handleFormSubmit = async (e, type) => {
        e.preventDefault()
        setLoading(true)
        const token = localStorage.getItem("token")
        try {
            if (type === 'new') {
                await createRequest({ title: newRequestTitle, category: newRequestCategory.value, description: newRequestDescription, status: "submitted" }, token)
            } else if (type === 'issue') {
                await reportIssue({ equipmentId: issueEquipment, issueType: issueType.value, description: issueDescription, status: "reported" }, token)
            } else if (type === 'form') {
                await createFormRequest({ formName, quantity: formQuantity, reason: formReason, status: "submitted" }, token)
            }
            toast.success("Submitted successfully")
            setActiveTab("myRequests")
            fetchAllData()
            // Reset forms
            setNewRequestTitle(""); setNewRequestCategory(null); setNewRequestDescription("");
            setIssueEquipment(""); setIssueType(null); setIssueDescription("");
            setFormName(""); setFormQuantity(1); setFormReason("");
        } catch (err) { toast.error("Submission failed") } finally { setLoading(false) }
    }

    const tabs = [
        { id: "myRequests", label: "History", icon: History },
        { id: "requestNew", label: "Requisition", icon: PlusCircle },
        { id: "reportIssue", label: "Report Issue", icon: AlertTriangle },
        { id: "assignedEquipment", label: "Assets", icon: Package },
        { id: "requestForms", label: "Forms", icon: FileStack },
    ];

    const selectStyles = {
        control: (base) => ({
            ...base,
            borderRadius: '0.75rem',
            padding: '0.25rem',
            border: 'none',
            backgroundColor: '#f9fafb',
            fontSize: '0.875rem',
            fontWeight: '600',
            boxShadow: 'none',
        }),
        option: (base, state) => ({
            ...base,
            fontSize: '0.875rem',
            fontWeight: '600',
            backgroundColor: state.isSelected ? '#ea580c' : state.isFocused ? '#fff7ed' : 'transparent',
            color: state.isSelected ? 'white' : '#374151',
        })
    };

    return (
        <div className="flex h-screen bg-gray-50 font-sans overflow-hidden">
            <MasterSideBar 
                visible={sidebarOpen} onClose={() => setSidebarOpen(false)} 
                collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}
                onLogout={() => { localStorage.clear(); window.location.href = "/"; }}
            />

            <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0 md:ml-64'}`}>
                <Topbar onSidebarToggle={() => setSidebarOpen(true)} onSearchChange={() => {}} />

                <main className="flex-1 overflow-y-auto p-4 md:p-8 mt-16 lg:mt-20">
                    <div className="max-w-6xl mx-auto">
                        <div className="mb-10">
                            <PremiumHeader 
                                title="Staff" 
                                lastWordAccent="Procurement" 
                                subtitle="Request resources, track your requisition history, and manage assigned assets." 
                            />
                        </div>

                        {/* Custom Tab Navigation */}
                        <div className="flex flex-wrap gap-2 mb-10 bg-white p-2 rounded-2xl shadow-sm border border-gray-100 max-w-fit">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`flex items-center gap-2 px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === tab.id ? "bg-orange-600 text-white shadow-lg shadow-orange-100" : "text-gray-400 hover:bg-orange-50 hover:text-orange-600"}`}
                                >
                                    <tab.icon size={14} /> {tab.label}
                                </button>
                            ))}
                        </div>

                        <div className="space-y-6">
                            {/* MY REQUESTS TAB */}
                            {activeTab === "myRequests" && (
                                <PremiumCard title="Requisition History" icon={<History size={18} />}>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left">
                                            <thead className="bg-gray-50/50">
                                                <tr>
                                                    <th className="px-6 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Requisition Title</th>
                                                    <th className="px-6 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Current Status</th>
                                                    <th className="px-6 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] text-right">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-50">
                                                {requests.length > 0 ? requests.map((r) => (
                                                    <tr key={r.id || r._id} className="hover:bg-orange-50/30 transition-colors">
                                                        <td className="px-6 py-4">
                                                            <div className="flex flex-col">
                                                                <span className="text-sm font-bold text-gray-800">{r.title}</span>
                                                                <span className="text-[10px] text-gray-400 font-bold uppercase tracking-tight">{r.category || 'Standard'}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <span className="px-4 py-1.5 bg-orange-50 text-orange-600 text-[10px] font-black uppercase tracking-widest rounded-full">{r.status}</span>
                                                        </td>
                                                        <td className="px-6 py-4 text-right">
                                                            <button onClick={() => setRequestToView(r)} className="p-3 bg-gray-50 text-gray-400 hover:bg-orange-600 hover:text-white rounded-xl transition-all active:scale-95"><Eye size={16} /></button>
                                                        </td>
                                                    </tr>
                                                )) : (
                                                    <tr>
                                                        <td colSpan="3" className="px-6 py-20 text-center">
                                                            <div className="flex flex-col items-center opacity-20">
                                                                <FileText size={48} className="mb-4" />
                                                                <p className="text-sm font-black uppercase tracking-widest">No history found</p>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </PremiumCard>
                            )}

                            {/* REQUISITION TAB */}
                            {activeTab === "requestNew" && (
                                <PremiumCard title="New Resource Request" icon={<PlusCircle size={18} />}>
                                    <form onSubmit={(e) => handleFormSubmit(e, 'new')} className="p-8 space-y-6 max-w-2xl">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Proposed Title</label>
                                            <input required value={newRequestTitle} onChange={e => setNewRequestTitle(e.target.value)} placeholder="e.g. Office Supplies for Q3" className="w-full bg-gray-50 border-none rounded-xl p-4 text-sm font-bold focus:ring-2 focus:ring-orange-500 transition-all placeholder:text-gray-300" />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Resource Category</label>
                                            <Select options={ITEM_CATEGORY_OPTIONS} value={newRequestCategory} onChange={setNewRequestCategory} styles={selectStyles} placeholder="Select Category..." />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Justification</label>
                                            <textarea required rows="4" value={newRequestDescription} onChange={e => setNewRequestDescription(e.target.value)} placeholder="Explain why these items are necessary for your work..." className="w-full bg-gray-50 border-none rounded-xl p-4 text-sm font-bold focus:ring-2 focus:ring-orange-500 transition-all placeholder:text-gray-300" />
                                        </div>
                                        <button disabled={loading} className="w-full py-5 bg-orange-600 text-white text-xs font-black uppercase tracking-[0.2em] rounded-2xl shadow-xl shadow-orange-100 hover:bg-orange-700 hover:-translate-y-0.5 transition-all active:scale-95 disabled:opacity-50">Submit Request</button>
                                    </form>
                                </PremiumCard>
                            )}

                            {/* REPORT ISSUE TAB */}
                            {activeTab === "reportIssue" && (
                                <PremiumCard title="Report Asset Issue" icon={<AlertTriangle size={18} />}>
                                    <form onSubmit={(e) => handleFormSubmit(e, 'issue')} className="p-8 space-y-6 max-w-2xl">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Target Asset</label>
                                            <Select 
                                                options={assignedEquipment.map(eq => ({ value: eq.id || eq._id, label: `${eq.name} (${eq.serial || eq.serialNumber || 'No Serial'})` }))} 
                                                onChange={(opt) => setIssueEquipment(opt?.value)} 
                                                styles={selectStyles}
                                                placeholder="Select Assigned Equipment..."
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Issue Severity / Type</label>
                                            <Select options={ISSUE_TYPE_OPTIONS} value={issueType} onChange={setIssueType} styles={selectStyles} placeholder="What went wrong?" />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Issue Details</label>
                                            <textarea required rows="4" value={issueDescription} onChange={e => setIssueDescription(e.target.value)} placeholder="Describe the damage or malfunction in detail..." className="w-full bg-gray-50 border-none rounded-xl p-4 text-sm font-bold focus:ring-2 focus:ring-orange-500 transition-all placeholder:text-gray-300" />
                                        </div>
                                        <button disabled={loading} className="w-full py-5 bg-red-600 text-white text-xs font-black uppercase tracking-[0.2em] rounded-2xl shadow-xl shadow-red-100 hover:bg-red-700 hover:-translate-y-0.5 transition-all active:scale-95 disabled:opacity-50">Report Asset Issue</button>
                                    </form>
                                </PremiumCard>
                            )}

                            {/* ASSIGNED EQUIPMENT TAB */}
                            {activeTab === "assignedEquipment" && (
                                <PremiumCard title="Assigned Organizational Assets" icon={<Package size={18} />}>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left">
                                            <thead className="bg-gray-50/50">
                                                <tr>
                                                    <th className="px-6 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Asset Name</th>
                                                    <th className="px-6 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Serial Identifier</th>
                                                    <th className="px-6 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Operational Status</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-50">
                                                {assignedEquipment.length > 0 ? assignedEquipment.map((item) => (
                                                    <tr key={item.id || item._id} className="hover:bg-orange-50/30 transition-colors">
                                                        <td className="px-6 py-4 text-sm font-bold text-gray-800">{item.name}</td>
                                                        <td className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest">{item.serial || item.serialNumber || 'N/A'}</td>
                                                        <td className="px-6 py-4">
                                                            <span className="px-4 py-1.5 bg-emerald-50 text-emerald-600 text-[10px] font-black uppercase tracking-widest rounded-full">{item.status || 'Active'}</span>
                                                        </td>
                                                    </tr>
                                                )) : (
                                                    <tr>
                                                        <td colSpan="3" className="px-6 py-20 text-center">
                                                            <div className="flex flex-col items-center opacity-20">
                                                                <Package size={48} className="mb-4" />
                                                                <p className="text-sm font-black uppercase tracking-widest">No assets assigned</p>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </PremiumCard>
                            )}

                            {/* REQUEST FORMS TAB */}
                            {activeTab === "requestForms" && (
                                <PremiumCard title="Official Form Replacements" icon={<FileStack size={18} />}>
                                    <form onSubmit={(e) => handleFormSubmit(e, 'form')} className="p-8 space-y-6 max-w-2xl">
                                        <div className="grid grid-cols-2 gap-6">
                                            <div className="col-span-2 md:col-span-1 space-y-2">
                                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Form Specification</label>
                                                <input required value={formName} onChange={e => setFormName(e.target.value)} placeholder="e.g. GRN Booklet" className="w-full bg-gray-50 border-none rounded-xl p-4 text-sm font-bold focus:ring-2 focus:ring-orange-500 transition-all placeholder:text-gray-300" />
                                            </div>
                                            <div className="col-span-2 md:col-span-1 space-y-2">
                                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Volume / Quantity</label>
                                                <input type="number" min="1" value={formQuantity} onChange={e => setFormQuantity(e.target.value)} className="w-full bg-gray-50 border-none rounded-xl p-4 text-sm font-bold focus:ring-2 focus:ring-orange-500 transition-all" />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Reason for Replacement</label>
                                            <textarea required rows="3" value={formReason} onChange={e => setFormReason(e.target.value)} placeholder="Explain the depletion or damage..." className="w-full bg-gray-50 border-none rounded-xl p-4 text-sm font-bold focus:ring-2 focus:ring-orange-500 transition-all placeholder:text-gray-300" />
                                        </div>
                                        <button disabled={loading} className="w-full py-5 bg-orange-600 text-white text-xs font-black uppercase tracking-[0.2em] rounded-2xl shadow-xl shadow-orange-100 hover:bg-orange-700 hover:-translate-y-0.5 transition-all active:scale-95 disabled:opacity-50">Submit Form Request</button>
                                    </form>
                                </PremiumCard>
                            )}
                        </div>
                    </div>
                </main>
            </div>

            {/* VIEW MODAL */}
            <Transition appear show={!!requestToView} as={Fragment}>
                <Dialog as="div" className="relative z-[100]" onClose={() => setRequestToView(null)}>
                    <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
                        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                    </Transition.Child>
                    
                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4">
                            <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-[2.5rem] bg-white p-10 shadow-2xl transition-all border border-gray-100">
                                    <div className="flex justify-between items-center mb-10">
                                        <div className="flex items-center gap-4">
                                            <div className="size-12 rounded-2xl bg-orange-50 text-orange-600 flex items-center justify-center">
                                                <ClipboardCheck size={24} />
                                            </div>
                                            <div>
                                                <Dialog.Title className="text-xs font-black text-gray-800 uppercase tracking-[0.2em]">Request Details</Dialog.Title>
                                                <p className="text-[10px] font-bold text-gray-400 uppercase mt-0.5">Application Overview</p>
                                            </div>
                                        </div>
                                        <button onClick={() => setRequestToView(null)} className="p-2 bg-gray-50 text-gray-400 hover:bg-gray-100 hover:text-gray-800 rounded-xl transition-all active:scale-95"><X size={20} /></button>
                                    </div>

                                    <div className="space-y-6">
                                        <div className="p-6 bg-gray-50 rounded-[2rem] border border-gray-100 shadow-inner">
                                            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 flex items-center gap-2">
                                                <div className="size-1.5 rounded-full bg-gray-300"></div> Requisition Title
                                            </p>
                                            <p className="font-bold text-gray-800 text-base">{requestToView?.title}</p>
                                        </div>
                                        <div className="p-6 bg-gray-50 rounded-[2rem] border border-gray-100 shadow-inner">
                                            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 flex items-center gap-2">
                                                <div className="size-1.5 rounded-full bg-orange-500"></div> Current Status
                                            </p>
                                            <p className="font-black text-orange-600 uppercase tracking-widest text-sm">{requestToView?.status}</p>
                                        </div>
                                        
                                        {requestToView?.description && (
                                            <div className="p-6 bg-gray-50 rounded-[2rem] border border-gray-100 shadow-inner">
                                                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 flex items-center gap-2">
                                                    <div className="size-1.5 rounded-full bg-gray-300"></div> Justification
                                                </p>
                                                <p className="text-sm font-bold text-gray-600 leading-relaxed">{requestToView?.description}</p>
                                            </div>
                                        )}
                                    </div>

                                    <button 
                                        onClick={() => setRequestToView(null)}
                                        className="w-full mt-10 py-5 bg-gray-900 text-white text-[10px] font-black uppercase tracking-[0.3em] rounded-2xl shadow-xl shadow-gray-100 hover:bg-orange-600 transition-all active:scale-95"
                                    >
                                        Dismiss Overview
                                    </button>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </div>
    )
}

export default ProgramStaffProcurement;